import type { ActorEvent, TimerEventProtocol } from '@lighthouse/core'
import { TIMER_TRIGGER_MODE } from '@lighthouse/core'
import { useEffect, useRef } from 'react'

import { usePageActive } from '../contexts'
import { resolveTimerIntervalMs } from '../utils/timerEvent'
import { useActionRunningLoadings } from './useActionRunningLoadings'

interface UsePageTimerEventPayload {
    timerEvent: TimerEventProtocol | undefined
    stackId: string
    trigger: (id: string, actorEvent: ActorEvent) => Promise<void>
}

export const usePageTimerEvent = ({ timerEvent, stackId, trigger }: UsePageTimerEventPayload) => {
    const isPageActive = usePageActive()
    const { handleActionTriggerWithLoading } = useActionRunningLoadings()

    const triggerRef = useRef(trigger)
    const handleActionTriggerRef = useRef(handleActionTriggerWithLoading)
    const timerEventRef = useRef(timerEvent)

    triggerRef.current = trigger
    handleActionTriggerRef.current = handleActionTriggerWithLoading
    timerEventRef.current = timerEvent

    const intervalMs = resolveTimerIntervalMs(timerEvent?.config)
    const triggerMode = timerEvent?.config?.triggerMode ?? TIMER_TRIGGER_MODE.ONCE
    const hasActions = Boolean(timerEvent?.actions?.length)

    useEffect(() => {
        if (!isPageActive || !hasActions || intervalMs <= 0) {
            return
        }

        const fire = () => {
            const currentEvent = timerEventRef.current
            if (!currentEvent) {
                return
            }
            handleActionTriggerRef.current({
                actorEvent: currentEvent,
                id: stackId,
                trigger: (id, actorEvent) => triggerRef.current(id, actorEvent)
            })
        }

        if (triggerMode === TIMER_TRIGGER_MODE.ONCE) {
            const timeoutId = window.setTimeout(fire, intervalMs)
            return () => window.clearTimeout(timeoutId)
        }

        const intervalId = window.setInterval(fire, intervalMs)
        return () => window.clearInterval(intervalId)
    }, [hasActions, intervalMs, isPageActive, stackId, triggerMode])
}
